import {ChangeDetectorRef, Component, Inject, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {StringUtils} from '@application/helper/string-utils';
import {Subscription} from '@domain/profile/subscription';
import {PropertyValue} from '@domain/property-value';
import {PageUrls} from '@domain/textile-data/page-urls';
import {WeaveStructure} from '@domain/textile-data/weave-structure/weave-structure';
import {Authentication, AUTHENTICATION} from '@infrastructure/http/authentication/authentication';
import {PRODUCT_CONFIGURATIONS, ProductConfigurations} from '@infrastructure/http/product-configuration/product-configuration.interface';
import {TextileDataOverviewComponent} from '@presentation/pages/textile-data/textile-data-overview/textile-data-overview.component';
import {TextileDataType} from '@presentation/pages/textile-data/textile-data-type.enum';
import {BaseComponent, ColDefBuilderFactoryService, DialogBuilder, DialogBuilderFactoryService, DialogType, GridModel, LinkIconRendererComponent} from '@vdw/angular-component-library';
import {AgGridAngular} from 'ag-grid-angular';
import {ColDef, GridOptions, ICellRendererParams, IMultiFilterModel, IServerSideDatasource, IServerSideGetRowsParams, SortModelItem, ValueGetterParams} from 'ag-grid-community';
import {Observable} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {OverviewListProductConfiguration} from './overview-list-product-configuration';

@Component({
  templateUrl: './product-configuration-overview-page.component.html',
  styleUrls: ['./product-configuration-overview-page.component.scss']
})
export class ProductConfigurationOverviewPageComponent extends BaseComponent implements OnInit {
  @ViewChild('textileDataOverview') public textileDataOverview: TextileDataOverviewComponent;
  @ViewChild('exportProductConfigurationsGrid') public exportProductConfigurationsGrid: AgGridAngular;
  public advancedSearchFilters: PropertyValue[] = [];
  public loadingForTheFirstTime = true;
  public dataSource: IServerSideDatasource;
  public exportGridOptions: GridOptions = {};
  public exportData: OverviewListProductConfiguration[] = [];
  public isExporting = false;
  public readonly textileDataType = TextileDataType;
  public readonly GRID_IDENTIFIER = GridIdentifier.PRODUCT_CONFIGURATION_OVERVIEW;
  public readonly quantityOfRowsToRequestPerSlice = 10;

  private pageUrls: PageUrls;
  private dialogBuilder: DialogBuilder;
  private currentSubscription: Subscription;

  public constructor(
    @Inject(PRODUCT_CONFIGURATIONS) public productConfigurations: ProductConfigurations,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    @Inject(AUTHENTICATION) private readonly authentication: Authentication
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
    this.pageUrls = LastModifiedCardUtils.getPageUrls('productConfiguration');
    this.dataSource = this.loadProductionConfigurations();
  }

  public getColumnDefsForListOfProductConfigurations(): ColDef[] {
    return [
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('name', true)
        .withHeaderName('GENERAL.NAME')
        .withComparator(StringUtils.stringComparator)
        .withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
          return {
            params,
            editPageUrl: this.pageUrls.edit
          };
        })
        .withTextMultiFilter(this.getPossibleColumnsValues('name'))
        .withLockVisible()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('machine')
        .withField('machine.name', true)
        .withHeaderName('MACHINE.MACHINE')
        .withTextMultiFilter(this.getPossibleColumnsValues('machine'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('machineQuality')
        .withHeaderName('TEXTILE_DATA.MACHINE_QUALITY.MACHINE_QUALITY')
        .withValueGetter((params: ValueGetterParams) => this.getValueWithVersionCell(params.data.quality, true), true)
        .withTextMultiFilter(this.getPossibleColumnsValues('machineQuality'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('weaveStructure')
        .withHeaderName('TEXTILE_DATA.WEAVE_STRUCTURE.WEAVE_STRUCTURE')
        .withValueGetter((params: ValueGetterParams) => this.getValueWithVersionCell(params.data.quality.singleWidth.weaveStructure), true)
        .withTextMultiFilter(this.getPossibleColumnsValues('weaveStructure'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('helpWeaveStructure')
        .withHeaderName('PRODUCT_CONFIGURATION.HELP_WEAVE_STRUCTURE')
        .withValueGetter((params: ValueGetterParams) => params.data.helpWeaveStructures.map((helpWeaveStructure: WeaveStructure) => this.getValueWithVersionCell(helpWeaveStructure)).join(', '), true)
        .withTextMultiFilter(this.getPossibleColumnsValues('helpWeaveStructure'))
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('designs')
        .withHeaderName('DESIGN_LIBRARY.DESIGN', 2, true)
        .withValueGetter((params: ValueGetterParams) => params.data.designs.length, true)
        .withRightAlignment()
        .withSortable(false)
        .withoutFilter()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColIdAndField('description', true)
        .withHeaderName('GENERAL.DESCRIPTION')
        .withTextMultiFilter(this.getPossibleColumnsValues('description'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withField('dateModified')
        .withHeaderName('GENERAL.DATETIME.DATE')
        .withDate()
        .withDateMultiFilter(this.getPossibleColumnsValues('dateModified'))
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('colorSet')
        .withField('colorSet.name', true)
        .withHeaderName('TEXTILE_DATA.COLOR_SET.COLOR_SET')
        .withTextMultiFilter(this.getPossibleColumnsValues('colorSet'))
        .withHide()
        .build(),
      this.colDefBuilderFactoryService
        .getBuilder()
        .withColId('weftColorSet')
        .withField('weftColorSet.name', true)
        .withHeaderName('PRODUCT_CONFIGURATION.WEFT_COLOR_SET')
        .withTextMultiFilter(this.getPossibleColumnsValues('weftColorSet'))
        .withHide()
        .build()
    ];
  }

  public loadProductionConfigurations(): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams): void => {
        const sortModel = params.request.sortModel;
        const startRow = params.request.startRow;
        const endRow = params.request.endRow;
        const filterModel = params.request.filterModel;

        const gridModel = new GridModel(startRow, endRow, sortModel, filterModel);

        this.productConfigurations
          .getSlice(gridModel)
          .pipe(takeUntil(this.unSubscribeOnViewDestroy))
          .subscribe({
            next: (listOfProductConfiguration: OverviewListProductConfiguration[]) => {
              let endRowConfiguration: number;

              if (listOfProductConfiguration.length < this.quantityOfRowsToRequestPerSlice) {
                endRowConfiguration = params.request.startRow + listOfProductConfiguration.length;
              }

              if (params.request.startRow === 0 && listOfProductConfiguration.length === 0) {
                this.textileDataOverview.showNoRowsOverlay();
              }

              this.loadingForTheFirstTime = false;

              this.changeDetectorRef.detectChanges();

              this.textileDataOverview.setLoadingCellRendererParams({
                loadingForTheFirstTime: this.loadingForTheFirstTime
              });

              params.success({
                rowData: listOfProductConfiguration,
                rowCount: endRowConfiguration
              });
            },
            error: (error: Error) => {
              params.fail();
              throw error;
            }
          });
      }
    };
  }

  public canShowCustomSettingsButton(): boolean {
    const requiredPermission = RouteUtils.paths.texEdit.productConfigurationSettings.requiredPermission;
    return this.currentSubscription?.hasPermission(requiredPermission);
  }

  public navigateToCustomSettings(): void {
    this.router.navigateByUrl(RouteUtils.paths.texEdit.productConfigurationSettings.absolutePath);
  }

  public reloadProductConfigurations(advancedSearchFilters: PropertyValue[]): void {
    this.advancedSearchFilters = advancedSearchFilters;
    this.textileDataOverview.hideOverlay();
    this.textileDataOverview.deselectRows();
    this.textileDataOverview.ensureIndexIsVisible(0);
    this.refreshServerSide(true);
  }

  public exportAllData(filterModel: IMultiFilterModel): void {
    this.dialogBuilder = this.dialogBuilderFactoryService.getBuilder();
    this.dialogBuilder.openAlertDialog({
      titleText: 'AGGRID.EXPORT',
      messageText: 'AGGRID.EXPORTMESSAGE',
      type: DialogType.INFORMATION
    });

    this.isExporting = true;
    this.exportProductConfigurationsGrid.api.setGridOption('columnDefs', this.getColumnDefsForListOfProductConfigurations());
    const sortModel: SortModelItem[] = [];
    let startRow: number = 0;
    let endRow: number = 30;
    const gridModel: GridModel = new GridModel(startRow, endRow, sortModel, filterModel);

    this.fetchExportData(gridModel);
  }

  private fetchExportData(gridModel: GridModel): void {
    this.productConfigurations
      .getSlice(gridModel)
      .pipe(takeUntil(this.unSubscribeOnViewDestroy))
      .subscribe((listOfProductConfigurations: OverviewListProductConfiguration[]) => {
        listOfProductConfigurations.forEach((productConfiguration: OverviewListProductConfiguration) => {
          this.exportData.push(productConfiguration);
        });

        if (listOfProductConfigurations.length < 29) {
          this.exportProductConfigurationsGrid.api.setGridOption('rowData', this.exportData);
          this.exportGridOptions.cacheBlockSize = this.exportData.length;
          this.exportProductConfigurationsGrid.api.exportDataAsCsv();
          this.exportData = [];
          this.isExporting = false;
          this.dialogBuilder.close();
        } else {
          gridModel.startRow += 30;
          gridModel.endRow += 30;
          this.fetchExportData(gridModel);
        }
      });
  }

  private refreshServerSide(purge: boolean): void {
    this.textileDataOverview.refreshServerSide(purge);
  }

  private getPossibleColumnsValues(columnIdentifier: string): Observable<string[]> {
    return this.productConfigurations.getPossibleValues(columnIdentifier).pipe(takeUntil(this.unSubscribeOnViewDestroy));
  }

  private getValueWithVersionCell(data: any, isQuality: boolean = false): string {
    const name = isQuality ? data.technicalName : data.name;
    return `${name} (v${data.version})`;
  }
}
